import { router } from '@inertiajs/react'
import type { ReactNode } from 'react'
import AppLayout from '@/layouts/AppLayout'
import AdminLayout from '@/layouts/AdminLayout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'

interface Conflict {
  id: string
  field_name: string
  existing_value: string | null
  existing_source: string
  incoming_value: string | null
  incoming_source: string
  status: string
  created_at: string
  provider: {
    id: string
    name: string
  }
}

interface Props {
  conflicts: Conflict[]
  counts: { pending: number; accepted: number; dismissed: number }
  current_status: string
}

const tabs = [
  { key: 'pending', label: 'Pending' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'dismissed', label: 'Dismissed' },
] as const

function timeAgo(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

export default function ConflictsIndex({ conflicts, counts, current_status }: Props) {
  function switchTab(status: string) {
    router.get('/admin/conflicts', { status }, { preserveState: true })
  }

  function accept(id: string) {
    router.post(`/admin/conflicts/${id}/accept`)
  }

  function dismiss(id: string) {
    router.post(`/admin/conflicts/${id}/dismiss`)
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-6">Conflicts</h1>

      {/* Status tabs */}
      <div className="flex gap-1 mb-6 border-b border-border">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => switchTab(tab.key)}
            className={cn(
              'flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors -mb-px',
              current_status === tab.key
                ? 'border-b-2 border-foreground text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {tab.label}
            <Badge
              variant={current_status === tab.key ? 'default' : 'secondary'}
              className="text-xs px-1.5 py-0"
            >
              {counts[tab.key]}
            </Badge>
          </button>
        ))}
      </div>

      {conflicts.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">
          No {current_status} conflicts.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Provider</TableHead>
              <TableHead>Field</TableHead>
              <TableHead>Existing</TableHead>
              <TableHead>Incoming</TableHead>
              <TableHead>Age</TableHead>
              {current_status === 'pending' && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {conflicts.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="font-medium">{c.provider.name}</TableCell>
                <TableCell>{c.field_name}</TableCell>
                <TableCell>
                  <div className="text-sm">{c.existing_value ?? <span className="text-muted-foreground italic">blank</span>}</div>
                  <div className="text-xs text-muted-foreground">{c.existing_source}</div>
                </TableCell>
                <TableCell>
                  <div className="text-sm">{c.incoming_value ?? <span className="text-muted-foreground italic">blank</span>}</div>
                  <div className="text-xs text-muted-foreground">{c.incoming_source}</div>
                </TableCell>
                <TableCell className="text-muted-foreground text-sm whitespace-nowrap">
                  {timeAgo(c.created_at)}
                </TableCell>
                {current_status === 'pending' && (
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="default" onClick={() => accept(c.id)}>
                        Accept
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => dismiss(c.id)}>
                        Dismiss
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  )
}

ConflictsIndex.layout = (page: ReactNode) => (
  <AppLayout>
    <AdminLayout>{page}</AdminLayout>
  </AppLayout>
)
